import { useLocation } from 'react-router-dom'
import { EventType, Origin, UseConfirmationActionsOrderDocument, UseConfirmationActionsOrderQuery } from '../graphql'

import getPhoenixUrlParams from '../lib/get-phoenix-url-params'
import Venue from '../typings/Venue'
import useOrder from './useOrder'

interface OrderData {
  venue: Venue
  hasBooking: boolean
  eventType?: EventType
  userRecordingId?: string
  invoiceDownloadLink?: string | null
  voucherCode?: string
}

const getBackToWebsiteLink = ({
  venue,
  hasBooking,
  eventType,
  userRecordingId,
  origin,
}: OrderData & { origin: Origin }) => {
  if ([Origin.ORIGIN_WIDGET, Origin.ORIGIN_PROVIDER_API].includes(origin)) {
    return `/widget/w/${venue.shortId}`
  }

  if (userRecordingId) {
    return `/s/${venue.slug}/videos`
  }

  if (hasBooking && eventType) {
    switch (eventType) {
      case EventType.CLASS:
        return `/scl/${venue.slug}`
      case EventType.COURSE:
        return `/sk/${venue.slug}`
      case EventType.WORKSHOP:
        return `/sw/${venue.slug}`
    }
  }

  return `/sp/${venue.slug}`
}

const getUserProfileLink = ({ venue, origin, hasBooking, userRecordingId }: OrderData & { origin: Origin }) => {
  if ([Origin.ORIGIN_WIDGET, Origin.ORIGIN_PROVIDER_API].includes(origin)) {
    return `/widget/w/${venue.shortId}`
  }

  if (userRecordingId) {
    return `/u/videos`
  }

  if (hasBooking) {
    return '/u'
  }

  return `/u/credits`
}

interface Result {
  goBackToWebsite: string
  goToMyProfile: string
  goToMyBookings: string
  goToModifySpots: string | null
  goToMyRecording: string | null
  hasSpotBooking: boolean
}

const useConfirmationActions = (): Result => {
  const { orderData, isOrderLoading } = useOrder<UseConfirmationActionsOrderQuery>(
    UseConfirmationActionsOrderDocument,
    { nextFetchPolicy: 'standby' },
  )

  const location = useLocation()
  const { origin } = getPhoenixUrlParams(location.search)

  if (!orderData || isOrderLoading) {
    return {
      goToMyProfile: '/u',
      goToMyBookings: '/u',
      goToModifySpots: null,
      goBackToWebsite: '/',
      goToMyRecording: null,
      hasSpotBooking: false,
    }
  }

  const hasBooking = orderData.order.items.some((item) => item.__typename === 'BookingOrderItem')
  const eventBooking = orderData.order.items.find((item) => item.__typename === 'BookingOrderItem')
  const eventType =
    (eventBooking &&
      eventBooking.__typename === 'BookingOrderItem' &&
      eventBooking.booking.__typename === 'EventBooking' &&
      eventBooking.booking.bookableItem.eventType) ||
    undefined

  const userRecordingOrderItem = orderData.order.items.find(
    (item) => item.__typename === 'AssignedProductOrderItem' && item.assignedProduct.__typename === 'AssignedRecording',
  )
  const userRecording =
    userRecordingOrderItem &&
    userRecordingOrderItem.__typename === 'AssignedProductOrderItem' &&
    userRecordingOrderItem.assignedProduct.__typename === 'AssignedRecording'
      ? userRecordingOrderItem.assignedProduct
      : undefined

  const venue = orderData.order.venue
  const companyId = venue.company.id
  const eventBookableItem =
    (eventBooking && eventBooking.booking.__typename === 'EventBooking' && eventBooking.booking.bookableItem) ||
    undefined

  const sessionParticipationId =
    eventBooking &&
    eventBooking.booking.__typename === 'EventBooking' &&
    eventBooking.booking.bookableItem &&
    eventBooking.booking.participations?.nodes?.[0]?.id

  const goToMyBookings = [Origin.ORIGIN_WIDGET, Origin.ORIGIN_PROVIDER_API].includes(origin)
    ? `/org/user/bookings?companyId=${companyId}&view=upcoming`
    : `/u`

  const goToModifySpots = sessionParticipationId
    ? [Origin.ORIGIN_WIDGET, Origin.ORIGIN_PROVIDER_API].includes(origin)
      ? `/org/user/bookings/${sessionParticipationId}/spots` // Go to Widget Next
      : `/user/bookings/${sessionParticipationId}/spots` // Go to Marketplace Modern
    : null

  const goToMyProfile = getUserProfileLink({ venue, hasBooking, userRecordingId: userRecording?.id, origin })
  const goBackToWebsite = getBackToWebsiteLink({
    venue,
    hasBooking,
    eventType,
    userRecordingId: userRecording?.id,
    origin,
  })

  const goToMyRecording = userRecording
    ? [Origin.ORIGIN_WIDGET, Origin.ORIGIN_PROVIDER_API].includes(origin)
      ? `/org/videos/${userRecording?.product.id}`
      : `/videos/${userRecording?.product.id}`
    : null

  return {
    goToMyBookings,
    goToMyProfile,
    goToModifySpots,
    goToMyRecording,
    goBackToWebsite,
    hasSpotBooking: Boolean(eventBookableItem?.floorPlan?.id),
  }
}

export default useConfirmationActions
